/**
 * Fallback content when Contentful `gymnasticMeets` fields are empty.
 * See docs/CONTENTFUL_MEET_FIELDS.md for the Contentful field list.
 */

/** Fixed section labels — not editable in Contentful. */
export const MEET_UI = {
  eyebrow: "Ace Gymnastics presents",
  introHeading: "The experience",
  highlightsHeading: "Why teams love this meet",
  galleryHeading: "Meet moments",
  faqHeading: "Frequently asked questions",
  faqIntro: "Quick answers for coaches, athletes, and families.",
  sponsorsHeading: "Our partners",
  sponsorsNote: "Thank you to the partners who help make this meet possible.",
  socialHeading: "Follow the action",
  socialText:
    "Tag us in your meet weekend photos and stay up to date on schedules and announcements.",
  closingTitle: "Ready to join us?",
  closingText:
    "Secure your spot at {title}. Registration is managed through our official meet registration link.",
};

export const MEET_HIGHLIGHTS = [
  {
    title: "USAG sanctioned",
    text: "Compete under official USA Gymnastics rules with experienced meet staff.",
  },
  {
    title: "All levels welcome",
    text: "Levels 1–10 and all Xcel divisions in one premier weekend event.",
  },
  {
    title: "Family atmosphere",
    text: "Music, energy, and a supportive environment for athletes and families.",
  },
  {
    title: "Hosted by Ace",
    text: "Presented by Ace Gymnastics — family-owned and rooted in Bardstown, KY.",
  },
];

/** Replace URLs with Contentful gallery assets when available */
export const MEET_GALLERY_PLACEHOLDERS = [
  {
    src: "https://images.unsplash.com/photo-1578662996442-48f60103fc96?w=800&q=80",
    alt: "Placeholder — gymnast on floor exercise",
  },
  {
    src: "https://images.unsplash.com/photo-1518611012118-696072aa579a?w=800&q=80",
    alt: "Placeholder — gymnastics training",
  },
  {
    src: "https://images.unsplash.com/photo-1594381898411-846e7d193883?w=800&q=80",
    alt: "Placeholder — bars practice",
  },
  {
    src: "https://images.unsplash.com/photo-1526232761682-d26e03ac731e?w=800&q=80",
    alt: "Placeholder — team celebration",
  },
  {
    src: "https://images.unsplash.com/photo-1541534741688-607811c06432?w=800&q=80",
    alt: "Placeholder — beam routine",
  },
  {
    src: "https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=800&q=80",
    alt: "Placeholder — vault approach",
  },
];

export const MEET_QUICK_STATS = [
  { value: "500+", label: "Athletes expected" },
  { value: "L1–10", label: "& all Xcel divisions" },
  { value: "3 days", label: "Of competition" },
  { value: "USAG", label: "Sanctioned meet" },
];

export const MEET_FAQS = [
  {
    q: "Who can compete at Bourbon City Spectacular?",
    a: "The meet welcomes USA Gymnastics athletes in Levels 1–10 and all Xcel divisions. Check registration details for session assignments and level requirements.",
  },
  {
    q: "How do I register my gym or athletes?",
    a: "Use the Register button on this page to complete registration through our official meet registration system.",
  },
  {
    q: "Where is the meet held?",
    a: "Venue and address details are listed in the Location section above. We recommend booking hotels early during meet weekend.",
  },
  {
    q: "What are spectator admission prices?",
    a: "Admission pricing is listed under Entry & Admission. Children 5 and under are typically free — see Contentful for current rates.",
  },
  {
    q: "Who do I contact with questions?",
    a: "See the Contacts section for meet leadership phone and email. For registration issues, include your gym name and athlete levels.",
  },
];

export const MEET_SPONSOR_PLACEHOLDERS = [
  "Community partner",
  "Local business",
  "Tourism partner",
  "Equipment partner",
];

export const MEET_SOCIAL = {
  instagram: "https://www.instagram.com/ace.gymnastics/",
  facebook:
    "https://www.facebook.com/ACE-Gymnastics-101806134601903/",
};

export const MEET_VENUE = {
  name: "Ace Gymnastics",
  street: "2388 New Haven Road",
  cityStateZip: "Bardstown, KY 40004",
  phone: "502-252-8512",
  mapQuery: "2388+New+Haven+Road+Bardstown+KY+40004",
  directionsUrl:
    "https://www.google.com/maps/dir/?api=1&destination=2388+New+Haven+Road+Bardstown+KY+40004",
};

export const MEET_VISIT = {
  heading: "Explore Bardstown",
  tagline: "Bourbon Capital of the World",
  intro:
    "Make a weekend of it. Bardstown is one of America's most beautiful small towns — walkable downtown, bourbon history, and great food. Many teams stay Friday through Sunday. Below are hotels near the venue and local spots our meet families love.",
  tourismUrl: "https://visitbardstown.com/",
  highlights: [
    {
      title: "Historic downtown",
      text: "Shops, distilleries, and architecture along Main Street — easy to explore between sessions.",
    },
    {
      title: "Bourbon country",
      text: "World-famous distilleries and tours a short drive from the venue.",
    },
    {
      title: "Family-friendly",
      text: "Restaurants and attractions that work for athletes, siblings, and grandparents.",
    },
    {
      title: "Plan ahead",
      text: "Hotels and popular dinner spots book up quickly on meet weekend — reserve early.",
    },
  ],
  hotelsHeading: "Stay near the meet",
  hotelsIntro:
    "Driving distances are calculated from the competition venue. Contact hotels directly for meet-weekend rates and availability.",
  restaurantsHeading: "Where to eat",
  restaurantsIntro:
    "Local favorites between sessions and after awards. Partner listings may include meet-weekend specials.",
  hotels: [
    {
      name: "Holiday Inn Express & Suites Bardstown",
      address: "985 Frost Ave, Bardstown, KY 40004",
      note: "Free hot breakfast and indoor pool. A frequent pick for traveling teams.",
      url: "https://www.ihg.com/holidayinnexpress/hotels/us/en/bardstown/bdwnk/hoteldetail",
    },
    {
      name: "Hampton Inn Bardstown",
      address: "985 Frost Ave, Bardstown, KY 40004",
      note: "Reliable chain option with breakfast included. Easy highway access.",
      url: "https://www.hilton.com/en/hotels/sdfbrhx-hampton-bardstown/",
    },
    {
      name: "Bardstown Park View Inn & Suites",
      address: "1035 Bloomfield Rd, Bardstown, KY 40004",
      note: "Close to Main Street restaurants and shops if you want to walk after sessions.",
      url: "https://www.bardstownparkviewinn.com/",
    },
    {
      name: "Old Talbott Tavern",
      address: "107 West Stephen Foster Ave, Bardstown, KY 40004",
      note: "Historic inn on the courthouse square — dining and lodging in one landmark building.",
      url: "https://www.talbotts.com/",
    },
  ],
  restaurants: [
    {
      name: "Talbott Tavern",
      address: "107 West Stephen Foster Ave, Bardstown, KY 40004",
      cuisine: "American · historic tavern",
      note: "Legendary spot on the square — burgers, Kentucky classics, and a memorable atmosphere.",
      url: "https://www.talbotts.com/",
    },
    {
      name: "Mammy's Kitchen & Pickles",
      address: "116 West Stephen Foster Ave, Bardstown, KY 40004",
      cuisine: "Southern · comfort food",
      note: "Bardstown institution. Hearty portions and friendly service — great for hungry teams.",
      url: "https://www.facebook.com/MammysKitchenandPickles/",
    },
    {
      name: "Rick's White Light Diner",
      address: "100 East Flaget Ave, Bardstown, KY 40004",
      cuisine: "Diner · breakfast & lunch",
      note: "Colorful local favorite featured on Diners, Drive-Ins and Dives. Go early on busy weekends.",
      url: "https://www.facebook.com/RicksWhiteLightDiner/",
    },
    {
      name: "Chappell's Dairy Mart",
      address: "1025 Bloomfield Rd, Bardstown, KY 40004",
      cuisine: "Burgers · ice cream",
      note: "Local icon for thick burgers and shakes — casual and kid-approved.",
      url: "https://www.facebook.com/ChappellsDairyMart/",
    },
    {
      name: "The Steward's Sons",
      address: "102 West Stephen Foster Ave, Bardstown, KY 40004",
      cuisine: "American · gastropub",
      note: "Upscale-casual dinner option with craft cocktails and a polished menu.",
      url: "https://www.thestewardssons.com/",
    },
    {
      name: "Barn8 Restaurant",
      address: "4089 Bardstown Rd, Bardstown, KY 40004",
      cuisine: "Farm-to-table · Hermitage Farm",
      note: "Special-occasion dining on a working farm — reserve ahead for Saturday night.",
      url: "https://www.barn8restaurant.com/",
    },
  ],
};
